import {
  useContext,
  useState,
  ReactNode,
  useEffect,
  KeyboardEvent,
  useCallback,
  useMemo,
  useRef,
} from "react";
import ArrowDropDown from "@servicetitan/hammer-icon/mdi/round/keyboard_arrow_down.svg";
import Event from "@servicetitan/hammer-icon/mdi/round/event.svg";
import { DateTime } from "luxon";
import {
  CustomFilter,
  DateFilter,
  DateRangeFilter,
  Filter,
  MultiSelectFilter,
  SingleSelectFilter,
} from "./internal/types";
import { FilterGroupContext } from "./internal/FilterGroupContext";
import {
  hasChangedFilter,
  hasFilterSelectionChanged,
} from "./internal/utils/filter-state";
import Chip from "../../../../components/Chip";
import { FilterSelect } from "./FilterSelect";
import { FilterDateSingle } from "./FilterDateSingle";
import { FilterDateRange } from "./FilterDateRange";
import { FilterItemWrapper } from "./FilterItemWrapper";
import Popover from "../../../../internal/components/Popover";
import Flex from "../../../../components/Flex";
import Button from "../../../../components/Button";

import { useToolbarSize } from "../internal/ToolbarContext";

import cx from "classnames";
import styles from "./Filters.module.scss";

/**
 * Props for the FilterButton component
 */
interface FilterButtonProps {
  /** Filter configuration object */
  filter: Filter;
  /** Optional child content for non-custom filter types */
  children?: ReactNode;
  /** Optional class name for the popover content */
  className?: string;
}

/**
 * FilterButton component provides a popover interface for filter selection.
 * Handles both controlled and uncontrolled filter updates.
 * Built into FilterGroup for single-select, multi-select, date, and date range filter rendering.
 *
 * Features:
 * - Maintains draft state for controlled filters
 * - Updates immediately for uncontrolled filters
 * - Provides Apply and Cancel actions in controlled mode
 * - Supports keyboard navigation and accessibility
 * - Renders custom, single-select, multi-select, date, and date range filter types
 * - Integrates with FilterGroup context for state management
 * - Displays appropriate icons for date-based filters
 * - Shows formatted date values in button labels
 */
export const FilterButton = ({
  filter,
  children,
  className,
}: FilterButtonProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const { updateFilter, controlledFiltering } = useContext(FilterGroupContext);
  const toolbarSize = useToolbarSize();

  // MultiSelect filters need to be initialized with an empty array
  const [draftValue, setDraftValue] = useState<unknown>(
    filter.type === "multiSelect" ? [] : undefined,
  );

  // Ref to track draftValue without causing handleChange to recreate
  const draftValueRef = useRef(draftValue);

  // Sync ref with state
  useEffect(() => {
    draftValueRef.current = draftValue;
  }, [draftValue]);

  /**
   * Returns the current value for the filter based on its type.
   * Used for resetting draft state and comparing changes.
   *
   * @returns The current filter value based on type:
   * - singleSelect: selectedItem
   * - multiSelect: selectedItems array
   * - date: date string value
   * - dateRange: { startDate, endDate } object
   * - custom: custom value
   */
  const getCurrentFilterValue = useCallback(() => {
    switch (filter.type) {
      case "singleSelect":
        return (filter as SingleSelectFilter<unknown>).selectedItem;
      case "multiSelect":
        return (filter as MultiSelectFilter<unknown>).selectedItems || [];
      case "date":
      case "dateRange":
        return filter.value;
      case "custom":
        return (filter as CustomFilter<unknown>).value;
      default:
        return undefined;
    }
  }, [filter]);

  // Track previous state to detect when to sync draft
  const prevIsOpenRef = useRef(isOpen);
  const prevFilterRef = useRef<Filter>(filter);

  // Initialize draft state when popover opens and sync when filter selection changes while open
  useEffect(() => {
    const justOpened = isOpen && !prevIsOpenRef.current;
    prevIsOpenRef.current = isOpen;

    if (isOpen) {
      // Sync draft state if:
      // 1. Popover just opened, OR
      // 2. Filter selection value changed while already open (race condition fix)
      // Don't sync when only items array changes (preserves selection during search)
      const shouldSync =
        justOpened ||
        (prevFilterRef.current &&
          hasFilterSelectionChanged(prevFilterRef.current, filter));

      if (shouldSync) {
        const currentValue = getCurrentFilterValue();
        setDraftValue(currentValue);
      }

      prevFilterRef.current = filter;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, filter]);

  const handleChange = useCallback(
    (value: unknown) => {
      if (hasChangedFilter(value, draftValueRef.current)) {
        // Sync ref immediately to guard against duplicate calls from ListView re-renders
        draftValueRef.current = value;
        // Always update draft state
        setDraftValue(value);

        // In uncontrolled mode, update immediately for all filter types
        // For multi-select, keep popover open to allow multiple selections
        // For all other types, close popover after updating
        if (!controlledFiltering) {
          updateFilter(filter.id, value, true);

          if (filter.type !== "multiSelect") {
            setIsOpen(false);
          }
        }
      }
    },
    [filter.id, filter.type, controlledFiltering, updateFilter],
  );

  const handleSubmit = useCallback(() => {
    const stateValue = getCurrentFilterValue();

    if (hasChangedFilter(draftValue, stateValue)) {
      updateFilter(filter.id, draftValue, true);
    }

    setIsOpen(false);
  }, [draftValue, getCurrentFilterValue, filter.id, updateFilter]);

  const handleCancel = useCallback(() => {
    // In uncontrolled mode, clicking outside should finalize changes, not cancel them
    if (!controlledFiltering) {
      setIsOpen(false);
      return;
    }

    const originalValue = getCurrentFilterValue();

    setDraftValue(originalValue);
    setIsOpen(false);
  }, [controlledFiltering, getCurrentFilterValue]);

  /**
   * Builds the button label text based on filter type and current selection.
   * Formats labels appropriately for each filter type to show current state.
   *
   * @returns Formatted label text:
   * - singleSelect: "Label: Selected Item" or just "Label"
   * - multiSelect: "Label" with selection count chip
   * - date: Full date string or "Label"
   * - dateRange: "Label: Start - End" or just "Label"
   * - custom: "Label: Custom Value" or just "Label"
   */
  const getButtonLabel = useMemo(() => {
    switch (filter.type) {
      case "singleSelect": {
        const singleSelectFilter = filter as SingleSelectFilter<{
          id: string;
          label: string;
        }>;
        if (singleSelectFilter.selectedItem) {
          return `${filter.label}: ${singleSelectFilter.selectedItem.label}`;
        }
        return filter.label;
      }
      case "multiSelect": {
        const multiSelectFilter = filter as MultiSelectFilter<{
          id: string;
          label: string;
        }>;
        if (
          multiSelectFilter.selectedItems &&
          multiSelectFilter.selectedItems.length > 0
        ) {
          const selectionCount =
            multiSelectFilter.selectedItems.length.toString();
          return (
            <>
              {filter.label}
              <Chip
                label={selectionCount}
                color="#0265DC"
                size="small"
                aria-label={`${selectionCount} selected items`}
              />
            </>
          );
        }
        return filter.label;
      }
      case "date":
        if (filter.value) {
          const date = DateTime.fromISO(filter.value, { zone: "local" });
          return `${filter.label}: ${date.monthShort} ${date.day}, ${date.year}`;
        }
        return filter.label;
      case "dateRange":
        if (filter.value?.startDate && filter.value?.endDate) {
          const start = DateTime.fromISO(filter.value.startDate as string, {
            zone: "local",
          });
          const end = DateTime.fromISO(filter.value.endDate as string, {
            zone: "local",
          });

          // If the start and end dates are in the same year, we don't shiw year in start date
          const isSameYear = start.year === end.year;
          const formattedStart = isSameYear
            ? `${start.monthShort} ${start.day}`
            : `${start.monthShort} ${start.day}, ${start.year}`;

          return `${filter.label}: ${formattedStart} – ${end.monthShort} ${end.day}, ${end.year}`;
        }
        return filter.label;
      case "custom":
        if (filter.labelChipCount) {
          return (
            <>
              {filter.label}
              <Chip
                label={filter.labelChipCount.toString()}
                color="#0265DC"
                size="small"
                aria-label={`${filter.labelChipCount} selected items`}
              />
            </>
          );
        }
        return filter.label;
      default:
        return filter.label;
    }
  }, [filter]);

  /**
   * Renders the appropriate filter content based on filter type.
   * Each filter type has its own specialized component for optimal UX.
   *
   * @returns React component for the filter type:
   * - custom: Custom render function
   * - singleSelect: FilterSelect with Listbox
   * - multiSelect: FilterSelect with ListView
   * - date: FilterDateSingle with calendar
   * - dateRange: FilterDateRange with range calendar
   */
  const getContent = useMemo(() => {
    switch (filter.type) {
      case "custom":
        return filter.buttonRender({
          value: draftValue,
          onChange: handleChange,
        });
      case "singleSelect":
        return (
          <FilterSelect
            filter={filter as SingleSelectFilter<{ id: string; label: string }>}
            draftValue={draftValue as { id: string; label: string } | undefined}
            onDraftChange={handleChange}
          />
        );
      case "multiSelect":
        return (
          <FilterSelect
            filter={filter as MultiSelectFilter<{ id: string; label: string }>}
            draftValue={
              draftValue as { id: string; label: string }[] | undefined
            }
            onDraftChange={handleChange}
          />
        );
      case "date":
        return (
          <FilterDateSingle
            filter={filter as DateFilter}
            draftValue={draftValue as string | null}
            onDraftChange={handleChange}
          />
        );
      case "dateRange":
        return (
          <FilterDateRange
            filter={filter as DateRangeFilter}
            draftValue={
              draftValue as {
                startDate: string | null;
                endDate: string | null;
              } | null
            }
            onDraftChange={handleChange}
          />
        );
      default:
        return children;
    }
  }, [filter, draftValue, handleChange, children]);

  const handleOutsidePress = () => {
    if (controlledFiltering) {
      handleCancel();
    } else if (filter.type === "multiSelect") {
      // Multi-select uncontrolled: update already called on each selection, just close
      setIsOpen(false);
    } else {
      // Other filter types uncontrolled: submit changes on close
      handleSubmit();
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Enter") {
      // In uncontrolled mode, Enter submits and closes for all filter types except multi-select
      // Multi-select updates on each selection, so Enter is a no-op to avoid re-submitting
      if (!controlledFiltering && filter.type !== "multiSelect") {
        handleSubmit();
      }
    }
  };

  const triggerClasses = cx(styles["filter-button-trigger"], {
    [styles["filter-button-trigger--selected"]]:
      (filter.type === "custom" && filter.value) ||
      (filter.type === "singleSelect" && filter.selectedItem) ||
      (filter.type === "multiSelect" &&
        filter.selectedItems &&
        filter.selectedItems.length > 0) ||
      (filter.type === "date" && filter.value) ||
      (filter.type === "dateRange" &&
        filter.value?.startDate &&
        filter.value?.endDate),
  });

  const contentClasses = cx(styles["filter-button-content"], className);

  const iconOptions =
    filter.type === "date" || filter.type === "dateRange"
      ? { before: Event }
      : { after: ArrowDropDown };

  return (
    <FilterItemWrapper filter={filter}>
      <Popover
        open={isOpen}
        onClose={handleOutsidePress}
        placement="bottom-start"
        modal
        noPadding={
          filter.type === "multiSelect" || filter.type === "singleSelect"
        }
      >
        <Popover.Button
          appearance="ghost"
          size={toolbarSize}
          icon={iconOptions}
          data-id={filter.id}
          data-anv="toolbar-button"
          className={triggerClasses}
          onClick={() => {
            setIsOpen(!isOpen);
          }}
        >
          {getButtonLabel}
        </Popover.Button>

        <Popover.Content onKeyDown={handleKeyDown} className={contentClasses}>
          {getContent}
          {controlledFiltering && (
            <Flex
              gap={2}
              justifyContent="flex-end"
              className={styles["filter-button-buttons"]}
            >
              <Popover.Close size="small">Cancel</Popover.Close>
              <Button appearance="primary" size="small" onClick={handleSubmit}>
                Apply
              </Button>
            </Flex>
          )}
        </Popover.Content>
      </Popover>
    </FilterItemWrapper>
  );
};
